/**
 * Source-seed handshake: the ONE-SHOT sessionStorage channel the Aircraft screen uses to hand
 * a CAD source (an example STEP path + a human label) to the Control Surfaces workbench.
 *
 * The Aircraft screen publishes the seed just before routing the user to 'control-surfaces';
 * ControlSurfacesSurface consumes it ONCE on mount (consume clears the key, so a reload never
 * re-applies a stale seed) - set the step-path input and, when a path is present, fire
 * segmentation immediately. sessionStorage (not localStorage) keeps the handoff scoped to the
 * tab. Mirrors features/highlift/foil/sectionSeed.ts (the CS -> high-lift section handoff).
 *
 * All storage access is try/caught: SSR (no `window`), private-mode quota errors and malformed
 * JSON all degrade to a silent no-op / `null` - the handshake is best-effort by design.
 */

/** Versioned storage key - bump the suffix if the seed record shape ever changes. */
const SOURCE_SEED_KEY = "thread.cs.sourceSeed.v1";

/**
 * The seed record. `stepPath` is absent when the publisher had no matching example on the
 * backend (examples dir unset) - the CS surface then just shows its normal empty source state.
 */
export interface CsSourceSeed {
  stepPath?: string;
  label?: string;
}

/** Minimal structural check that a parsed value is a usable {@link CsSourceSeed}. */
function isCsSourceSeed(value: unknown): value is CsSourceSeed {
  if (typeof value !== "object" || value === null) return false;
  const { stepPath, label } = value as { stepPath?: unknown; label?: unknown };
  if (stepPath !== undefined && typeof stepPath !== "string") return false;
  if (label !== undefined && typeof label !== "string") return false;
  // An empty record carries nothing worth applying - treat it as no seed.
  return typeof stepPath === "string" || typeof label === "string";
}

/**
 * Publish a source seed for the Control Surfaces workbench to pick up on its next mount.
 * One-shot: each publish REPLACES any unconsumed previous seed. Best-effort (quota/SSR
 * failures are swallowed).
 */
export function publishSourceSeed(seed: CsSourceSeed): void {
  try {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(SOURCE_SEED_KEY, JSON.stringify(seed));
  } catch {
    // Best-effort: storage unavailable (SSR / private mode / quota) - the handoff simply no-ops.
  }
}

/**
 * Consume the pending source seed, if any. CLEARS the key first (one-shot - even a malformed
 * record is cleared so a poisoned value can never wedge the mount path), then parses + validates.
 * Returns `null` when there is no seed, storage is unavailable, or the record is malformed.
 */
export function consumeSourceSeed(): CsSourceSeed | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.sessionStorage.getItem(SOURCE_SEED_KEY);
    if (raw == null) return null;
    window.sessionStorage.removeItem(SOURCE_SEED_KEY);
    const parsed: unknown = JSON.parse(raw);
    return isCsSourceSeed(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
